import classnames from 'classnames'
import nanoid from 'nanoid'
import React, { PureComponent } from 'react'
import ReactDOM from 'react-dom'

import css from './dialog.module.scss'

const cx = classnames.bind(css)

const Overlay = ({ children, ...rest }) => (
  <div className={cx(css.Overlay)} {...rest}>
    {children}
  </div>
)

const Root = ({ children, ...rest }) => (
  <section className={cx(css.Dialog)} {...rest}>
    {children}
  </section>
)

const Form = ({ children, ...rest }) => (
  <form className={cx(css.Form)} {...rest}>
    {children}
  </form>
)

const Header = ({ children, ...rest }) => (
  <form className={cx(css.Header)} {...rest}>
    {children}
  </form>
)

const Title = ({ children, ...rest }) => (
  <h2 className={cx(css.Title)} {...rest}>
    {children}
  </h2>
)

const HeaderCancelButton = ({ children, ...rest }) => (
  <button className={cx(css.HeaderCancelButton)} {...rest}>
    {children}
  </button>
)

const Content = ({ children, ...rest }) => (
  <div className={cx(css.Content)} {...rest}>
    {children}
  </div>
)

const Buttons = ({ children, ...rest }) => (
  <div className={cx(css.Buttons)} {...rest}>
    {children}
  </div>
)

interface DialogProps {
  innerRef: (element: HTMLElement | null) => void
  onCancel?: () => void
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  title: React.ReactNode
  buttons: React.ReactNode
  width?: string
}

export default class Dialog extends PureComponent<DialogProps, {}> {
  static displayName = 'Dialog'
  private titleId: string
  private container: HTMLElement
  private root: HTMLElement
  private form: HTMLFormElement

  static defaultProps = {
    onCancel: undefined,
    width: '750px',
  }

  constructor(props: DialogProps) {
    super(props)

    this.titleId = nanoid()
    this.container = document.createElement('div')
    this.container.setAttribute('data-consent-manager-dialog', '')

    document.body.appendChild(this.container)
  }

  render() {
    const { onCancel, onSubmit, title, children, buttons, width } = this.props

    const dialog = (
      <Overlay onClick={this.handleOverlayClick}>
        <Root
          innerRef={this.handleRootRef}
          role="dialog"
          aria-modal
          aria-labelledby={this.titleId}
          width={width}>
          <Header>
            <Title id={this.titleId}>{title}</Title>
            {onCancel && (
              <HeaderCancelButton
                onClick={onCancel}
                title="Cancel"
                aria-label="Cancel">
                ✕
              </HeaderCancelButton>
            )}
          </Header>

          <Form innerRef={this.handleFormRef} onSubmit={onSubmit}>
            <Content>{children}</Content>

            <Buttons>{buttons}</Buttons>
          </Form>
        </Root>
      </Overlay>
    )

    return ReactDOM.createPortal(dialog, this.container)
  }

  componentDidMount() {
    const { innerRef } = this.props

    if (this.form) {
      const input: HTMLInputElement | null = this.form.querySelector(
        'input,button',
      )
      if (input) {
        input.focus()
      }
    }

    document.body.addEventListener('keydown', this.handleEsc, false)
    document.body.style.overflow = 'hidden'
    innerRef(this.container)
  }

  componentWillUnmount() {
    const { innerRef } = this.props
    document.body.removeEventListener('keydown', this.handleEsc, false)
    document.body.style.overflow = ''
    document.body.removeChild(this.container)
    innerRef(null)
  }

  handleRootRef = (node: HTMLElement) => {
    this.root = node
  }

  handleFormRef = (node: HTMLFormElement) => {
    this.form = node
  }

  handleOverlayClick = (e) => {
    const { onCancel } = this.props
    // Ignore propogated clicks from inside the dialog
    if (onCancel && this.root && !this.root.contains(e.target)) {
      onCancel()
    }
  }

  handleEsc = (e: KeyboardEvent) => {
    const { onCancel } = this.props
    // Esc key
    if (onCancel && e.keyCode === 27) {
      onCancel()
    }
  }
}
